// Bank Withdrawal verification (legacy /AccountsApproval/VerifyBankWithdrawn).
// Pending rows are verified/approved via the shared status list (the withdrawal amount is passed
// as ChkAmt, so amount-based approval limits apply); rows returned for update (Approvalstatus '0')
// get legacy's resubmit form (name, bank, mode of pay, cheque/no, date, remarks, amount).
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { HandCoins, Clock, Building2, RotateCcw, Send } from 'lucide-react';

import InboxHeader from '../../components/Inbox/InboxHeader';
import ActionButtons from '../../components/Inbox/ActionButtons';
import InboxSplitLayout from '../../components/Inbox/InboxSplitLayout';
import { SimpleCommentInput } from '../../components/Inbox/VerificationInput';
import { formatIndianCurrency } from '../../utilities/amountToTextHelper';

import {
    fetchVerifyWithdrawalQueue,
    fetchVerifyWithdrawalDetail,
    fetchReturnedWithdrawalDetail,
    fetchWithdrawalBanks,
    fetchWithdrawalCheques,
    submitVerifyWithdrawal,
    submitUpdateWithdrawal,
    clearVerifyWithdrawalDetail,
    resetVerifyBankWithdrawal,
    selectVBWQueue, selectVBWQueueLoading, selectVBWQueueError,
    selectVBWDetail, selectVBWDetailLoading, selectVBWDetailError,
    selectVBWBanks, selectVBWCheques, selectVBWChequesLoading,
    selectVBWVerifyLoading, selectVBWUpdateLoading,
} from '../../slices/accountsSlice/verifyBankWithdrawalSlice';

import {
    fetchStatusList,
    selectEnabledActions,
    selectHasActions,
    selectStatusListLoading,
    selectStatusListError,
    resetApprovalData,
    setShowReturnButton,
} from '../../slices/CommonSlice/getStatusSlice';

const ACTION_VERB = {
    Verify: 'Verified Successfully',
    Approve: 'Approved Successfully',
    Return: 'Returned for Update',
    Reject: 'Rejected Successfully',
};

// Legacy's hardcoded pay-mode list (AccountsApprovalController.UpdateWithdrawnView).
const PAY_MODES = ['Cheque', 'Cash', 'RTGS/E-Trans', 'DD'];
const CHEQUE = 'Cheque';
// Legacy re-adds the withdrawal's current cheque as an option with value 0000; the SP treats
// ChequeId 0 as "keep the number in @No".
const KEEP_CURRENT_CHEQUE = '0';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const Field = ({ label, value, className = '' }) => (
    <div className={className}>
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">{label}</p>
        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 break-words">{value || value === 0 ? value : '—'}</p>
    </div>
);

const StatusBadge = ({ status }) => (String(status) === '0' ? (
    <span className="px-2 py-0.5 text-xs rounded-full border bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 whitespace-nowrap">Return</span>
) : (
    <span className="px-2 py-0.5 text-xs rounded-full border bg-indigo-100 text-indigo-800 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 whitespace-nowrap">Verification Pending</span>
));

const inputCls = 'w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500';
const labelCls = 'block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1';

const formatAmount = (v) => (v != null && v !== '' ? `₹ ${formatIndianCurrency(v)}` : null);
const todayIso = () => (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })();
// "25-Sep-2026" → "2026-09-25"
const legacyToIso = (v) => {
    const m = /^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/.exec(String(v || '').trim());
    if (!m) return '';
    const month = MONTHS.findIndex((x) => x.toLowerCase() === m[2].toLowerCase()) + 1;
    return month ? `${m[3]}-${String(month).padStart(2, '0')}-${m[1].padStart(2, '0')}` : '';
};
// The queue's Date is a raw TransactionDate; show it as dd-MMM-yyyy like the detail endpoint does.
const displayDate = (v) => {
    if (!v) return '';
    if (/^\d{1,2}-[A-Za-z]{3}-\d{4}$/.test(v)) return v;
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? v : `${String(d.getDate()).padStart(2, '0')}-${MONTHS[d.getMonth()]}-${d.getFullYear()}`;
};

const VerifyBankWithdrawal = ({ notificationData, onNavigate }) => {
    const dispatch = useDispatch();
    const { userData, userDetails } = useSelector((s) => s.auth);
    const roleId = userData?.roleId || userData?.RID;

    const queue = useSelector(selectVBWQueue);
    const queueLoading = useSelector(selectVBWQueueLoading);
    const queueError = useSelector(selectVBWQueueError);
    const detail = useSelector(selectVBWDetail);
    const detailLoading = useSelector(selectVBWDetailLoading);
    const detailError = useSelector(selectVBWDetailError);
    const banks = useSelector(selectVBWBanks);
    const cheques = useSelector(selectVBWCheques);
    const chequesLoading = useSelector(selectVBWChequesLoading);
    const verifyLoading = useSelector(selectVBWVerifyLoading);
    const updateLoading = useSelector(selectVBWUpdateLoading);

    const statusLoading = useSelector(selectStatusListLoading);
    const statusError = useSelector(selectStatusListError);
    const enabledActions = useSelector(selectEnabledActions);
    const hasActions = useSelector(selectHasActions);

    const { InboxTitle, ModuleDisplayName } = notificationData || {};

    const [selectedItem, setSelectedItem] = useState(null);
    const [comment, setComment] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);
    const [isLeftPanelHovered, setIsLeftPanelHovered] = useState(false);
    // Returned-row resubmit form
    const [upd, setUpd] = useState({ name: '', bankId: '', mode: '', no: '', chequeId: '', date: '', remarks: '', amount: '' });
    const setField = (key, value) => setUpd((prev) => ({ ...prev, [key]: value }));

    const getCurrentUser = () => userData?.userName || userDetails?.userName || 'system';

    const isReturned = String(selectedItem?.Approvalstatus) === '0';
    // Only trust the detail that belongs to the row currently selected.
    const withdrawal = selectedItem && detail?.Id === selectedItem.Id ? detail : null;

    useEffect(() => {
        if (roleId) dispatch(fetchVerifyWithdrawalQueue(roleId));
    }, [roleId, dispatch]);

    useEffect(() => {
        // Legacy's status dropdown on this page keeps every option, including "Return".
        dispatch(setShowReturnButton('Yes'));
        return () => {
            dispatch(resetVerifyBankWithdrawal());
            dispatch(resetApprovalData());
        };
    }, [dispatch]);

    useEffect(() => {
        if (!selectedItem) return;
        dispatch(clearVerifyWithdrawalDetail());
        dispatch(resetApprovalData());
        dispatch(setShowReturnButton('Yes'));
        if (String(selectedItem.Approvalstatus) === '0') {
            dispatch(fetchReturnedWithdrawalDetail(selectedItem.Id));
            if (!banks.length) dispatch(fetchWithdrawalBanks());
        } else {
            dispatch(fetchVerifyWithdrawalDetail(selectedItem.Id));
            // Queue row carries MOID; legacy passes the withdrawal amount as ChkAmt.
            if (selectedItem.MOID && roleId) {
                dispatch(fetchStatusList({ MOID: selectedItem.MOID, ROID: roleId, ChkAmt: selectedItem.Amount || 0 }));
            }
        }
        setComment('');
        setIsLeftPanelCollapsed(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedItem]);

    // Prefill the resubmit form once the returned withdrawal is in.
    useEffect(() => {
        if (!isReturned || !withdrawal) return;
        setUpd({
            name: withdrawal.Name || '',
            bankId: withdrawal.From ? String(withdrawal.From) : '',
            mode: withdrawal.Mode_of_Pay || '',
            no: withdrawal.No || '',
            chequeId: withdrawal.Mode_of_Pay === CHEQUE ? KEEP_CURRENT_CHEQUE : '',
            date: legacyToIso(withdrawal.Date) || String(withdrawal.Date || '').slice(0, 10),
            remarks: withdrawal.Remarks || '',
            amount: withdrawal.Amount != null ? String(withdrawal.Amount) : '',
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [withdrawal?.Id]);

    const selectedBankName = banks.find((b) => String(b.Bank_Id) === String(upd.bankId))?.Bank_Name;
    const isOriginalBank = withdrawal && String(withdrawal.From) === String(upd.bankId);

    // Cheque mode lists the chosen bank's unused cheques.
    useEffect(() => {
        if (!isReturned || upd.mode !== CHEQUE || !selectedBankName) return;
        dispatch(fetchWithdrawalCheques(selectedBankName));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [upd.mode, selectedBankName]);

    const handleBackToInbox = () => {
        if (onNavigate) onNavigate('dashboard', { name: 'Dashboard', type: 'dashboard' });
    };

    const handleRefresh = () => {
        if (roleId) dispatch(fetchVerifyWithdrawalQueue(roleId));
    };

    const finishAndRefresh = () => {
        setTimeout(() => {
            dispatch(fetchVerifyWithdrawalQueue(roleId));
            setSelectedItem(null);
            setComment('');
            setIsLeftPanelCollapsed(false);
            dispatch(clearVerifyWithdrawalDetail());
            dispatch(resetApprovalData());
        }, 800);
    };

    const handleActionClick = async (action) => {
        if (!withdrawal) { toast.error('No withdrawal selected'); return; }
        if (!comment.trim()) { toast.error('Enter Note'); return; }

        const actionValue = action.type;
        const payload = {
            Id: withdrawal.Id,
            Approvalstatus: actionValue,
            ApprovalRemarks: comment.trim(),
            Createdby: getCurrentUser(),
            RoleId: roleId,
        };

        try {
            const result = await dispatch(submitVerifyWithdrawal(payload)).unwrap();
            const status = typeof result === 'string' ? result : result?.Data;
            if (status === 'Submitted' || status === 'Submited') {
                toast.success(`Withdraw ${ACTION_VERB[actionValue] || `${actionValue}d`}`);
                finishAndRefresh();
                return;
            }
            toast.error(status || `${actionValue} was not applied — the server returned no confirmation.`);
        } catch (error) {
            toast.error(typeof error === 'string' ? error : 'Error Occurred While WithDraw Verification');
        }
    };

    const handleBankChange = (value) => {
        // A different bank's cheque book: the original cheque no longer applies.
        setUpd((prev) => ({ ...prev, bankId: value, chequeId: prev.mode === CHEQUE && String(withdrawal?.From) === value ? KEEP_CURRENT_CHEQUE : '' }));
    };

    const handleModeChange = (value) => {
        setUpd((prev) => ({
            ...prev,
            mode: value,
            chequeId: value === CHEQUE && isOriginalBank && withdrawal?.Mode_of_Pay === CHEQUE ? KEEP_CURRENT_CHEQUE : '',
            no: value === CHEQUE ? prev.no : (withdrawal?.Mode_of_Pay === CHEQUE ? '' : prev.no),
        }));
    };

    const handleAmountChange = (e) => {
        const value = e.target.value;
        if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) setField('amount', value);
    };

    const handleResubmit = async () => {
        if (!withdrawal) return;
        const errors = [];
        if (!upd.name.trim()) errors.push('Enter Name');
        if (!upd.bankId) errors.push('Select Withdrawal From');
        if (!upd.mode) errors.push('Select Mode of Pay');
        if (upd.mode === CHEQUE && upd.chequeId === '') errors.push('Select Cheque No');
        if (upd.mode && upd.mode !== CHEQUE && !upd.no.trim()) errors.push('Enter No');
        if (!upd.amount || parseFloat(upd.amount) === 0) errors.push('Enter Amount');
        if (!upd.remarks.trim()) errors.push('Enter Remarks');
        if (!upd.date) errors.push('Select Date');
        if (errors.length) { errors.forEach((m) => toast.error(m)); return; }

        const keepingCurrent = upd.mode === CHEQUE && upd.chequeId === KEEP_CURRENT_CHEQUE;
        const payload = {
            Id: withdrawal.Id,
            Name: upd.name.trim(),
            From: upd.bankId,
            Mode_of_Pay: upd.mode,
            No: upd.mode === CHEQUE ? (keepingCurrent ? withdrawal.No : '') : upd.no.trim(),
            Chequeid: upd.mode === CHEQUE ? upd.chequeId : '0',
            Date: upd.date,
            Remarks: upd.remarks.trim(),
            Amount: parseFloat(upd.amount),
            Createdby: getCurrentUser(),
            RoleID: roleId,
        };

        try {
            const result = await dispatch(submitUpdateWithdrawal(payload)).unwrap();
            const status = typeof result === 'string' ? result : result?.Data;
            // SPGeneralBankPaymentupdateWithdraw's own success literal is "Successfull".
            if (status === 'Successfull' || status === 'Submitted') {
                toast.success('Withdraw Updated Successfully');
                finishAndRefresh();
                return;
            }
            toast.error(status || 'Error Occurred While Return Withdraw Updation');
        } catch (error) {
            toast.error(typeof error === 'string' ? error : 'Error Occurred While Return Withdraw Updation');
        }
    };

    const filteredQueue = queue.filter((item) => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return item.Name?.toLowerCase().includes(q)
            || item.BankName?.toLowerCase().includes(q)
            || item.Modeofpay?.toLowerCase().includes(q)
            || String(item.No ?? '').toLowerCase().includes(q)
            || String(item.Amount ?? '').includes(q);
    });

    const renderItemCard = (item) => (
        <div className="p-4">
            <div className="flex items-center space-x-3 mb-2">
                <div className="w-12 h-12 rounded-full border-2 border-indigo-200 dark:border-indigo-600 bg-gradient-to-br from-indigo-100 to-indigo-100 dark:from-indigo-800/50 dark:to-indigo-800/50 flex items-center justify-center">
                    <HandCoins className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm truncate">{item.Name}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{displayDate(item.Date)}</p>
                </div>
                <span className="text-sm font-bold text-gray-900 dark:text-white whitespace-nowrap">{formatAmount(item.Amount)}</span>
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-1 min-w-0"><Building2 className="w-3 h-3 flex-shrink-0" /><span className="truncate">{item.BankName}</span></span>
                    <StatusBadge status={item.Approvalstatus} />
                </div>
            </div>
        </div>
    );

    const renderListItem = (item) => (
        <div className="flex items-center gap-x-6 gap-y-1 flex-wrap text-sm">
            <span className="font-semibold text-gray-900 dark:text-white min-w-[160px] truncate">{item.Name}</span>
            <span className="text-gray-500 dark:text-gray-400 truncate max-w-[240px]">{item.BankName}</span>
            <span className="text-gray-500 dark:text-gray-400 min-w-[90px]">{item.Modeofpay}</span>
            <span className="text-gray-500 dark:text-gray-400 min-w-[100px]">{displayDate(item.Date)}</span>
            <span className="font-semibold text-gray-900 dark:text-white">{formatAmount(item.Amount)}</span>
            <span className="ml-auto"><StatusBadge status={item.Approvalstatus} /></span>
        </div>
    );

    const renderCollapsedItem = () => (
        <div className="w-full h-full rounded-lg border-2 border-indigo-200 dark:border-indigo-600 bg-gradient-to-br from-indigo-100 to-indigo-100 dark:from-indigo-800/50 dark:to-indigo-800/50 flex items-center justify-center">
            <HandCoins className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
        </div>
    );

    const renderPendingDetails = () => (
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border-2 border-gray-200 dark:border-gray-700 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Field label="Name" value={withdrawal.Name} />
                <Field label="Bank" value={withdrawal.BankName} />
                <Field label="Payment Mode" value={withdrawal.Modeofpay} />
                <Field label="Cheque / Ref No" value={withdrawal.No} />
                <Field label="Payment Date" value={withdrawal.Date} />
                <Field label="Amount" value={formatAmount(withdrawal.Amount)} />
                <Field label="Amount in Words" value={withdrawal.AmountInWords} className="sm:col-span-2" />
                <Field label="Remarks" value={withdrawal.Remarks} className="sm:col-span-2 lg:col-span-4" />
            </div>
        </div>
    );

    const renderResubmitForm = () => (
        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-2xl p-5 border-2 border-amber-200 dark:border-amber-700 space-y-4">
            <div className="flex items-center gap-2">
                <RotateCcw className="w-4 h-4 text-amber-700 dark:text-amber-400" />
                <h3 className="text-sm font-bold text-amber-800 dark:text-amber-300">Returned for Update — correct and resubmit</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                    <label className={labelCls}>Name <span className="text-red-500">*</span></label>
                    <input type="text" className={inputCls} value={upd.name} onChange={(e) => setField('name', e.target.value)} />
                </div>
                <div>
                    <label className={labelCls}>Withdrawal From <span className="text-red-500">*</span></label>
                    <select className={inputCls} value={upd.bankId} onChange={(e) => handleBankChange(e.target.value)}>
                        <option value="">-Please Select-</option>
                        {banks.map((b) => <option key={b.Bank_Id} value={b.Bank_Id}>{b.Bank_Name}</option>)}
                    </select>
                </div>
                <div>
                    <label className={labelCls}>Mode of Pay <span className="text-red-500">*</span></label>
                    <select className={inputCls} value={upd.mode} onChange={(e) => handleModeChange(e.target.value)}>
                        <option value="">-Please Select-</option>
                        {PAY_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                </div>
                <div>
                    <label className={labelCls}>{upd.mode === CHEQUE ? 'Cheque No' : 'No'} <span className="text-red-500">*</span></label>
                    {upd.mode === CHEQUE ? (
                        <select className={inputCls} value={upd.chequeId} onChange={(e) => setField('chequeId', e.target.value)} disabled={chequesLoading}>
                            <option value="">{chequesLoading ? 'Loading…' : '-Please Select-'}</option>
                            {isOriginalBank && withdrawal?.Mode_of_Pay === CHEQUE && withdrawal?.No && (
                                <option value={KEEP_CURRENT_CHEQUE}>{withdrawal.No} (current)</option>
                            )}
                            {cheques.map((c) => <option key={c.Cheque_Id} value={c.Cheque_Id}>{c.Cheque_No}</option>)}
                        </select>
                    ) : (
                        <input type="text" className={inputCls} value={upd.no} onChange={(e) => setField('no', e.target.value)} />
                    )}
                </div>
                <div>
                    <label className={labelCls}>Date <span className="text-red-500">*</span></label>
                    <input type="date" className={inputCls} value={upd.date} max={todayIso()} onChange={(e) => setField('date', e.target.value)} />
                </div>
                <div>
                    <label className={labelCls}>Amount <span className="text-red-500">*</span></label>
                    <input type="text" inputMode="decimal" className={inputCls} value={upd.amount} onChange={handleAmountChange} />
                </div>
                <div className="sm:col-span-3">
                    <label className={labelCls}>Remarks <span className="text-red-500">*</span></label>
                    <textarea rows={3} className={inputCls} value={upd.remarks} onChange={(e) => setField('remarks', e.target.value)} />
                </div>
            </div>
            <div className="flex justify-end">
                <button
                    type="button"
                    onClick={handleResubmit}
                    disabled={updateLoading}
                    className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                    {updateLoading ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> : <Send className="w-4 h-4" />}
                    Resubmit
                </button>
            </div>
        </div>
    );

    const renderActions = () => {
        if (statusLoading) {
            return (
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-center space-x-3">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                        <span className="text-gray-600 dark:text-gray-400">Loading actions…</span>
                    </div>
                </div>
            );
        }
        if (statusError) {
            return (
                <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 border border-red-200 dark:border-red-700">
                    <p className="text-red-600 dark:text-red-400 text-center">⚠️ Error loading actions: {statusError}</p>
                </div>
            );
        }
        if (!hasActions || !enabledActions || enabledActions.length === 0) {
            return (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-xl p-6 border border-yellow-200 dark:border-yellow-700">
                    <p className="text-yellow-700 dark:text-yellow-400 text-center">ℹ️ No actions available for this withdrawal</p>
                </div>
            );
        }
        return (
            <ActionButtons
                actions={enabledActions}
                onActionClick={handleActionClick}
                loading={verifyLoading}
                isVerified={true}
                comment={comment}
                showValidation={true}
            />
        );
    };

    const renderDetailContent = () => {
        if (!selectedItem) return null;

        if (detailLoading || (!withdrawal && !detailError)) {
            return (
                <div className="flex items-center justify-center gap-3 py-12">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">Loading withdrawal details…</span>
                </div>
            );
        }
        if (detailError || !withdrawal) {
            return (
                <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 border border-red-200 dark:border-red-700">
                    <p className="text-red-600 dark:text-red-400 text-center">⚠️ {detailError || 'Withdrawal details could not be loaded — it may no longer be pending at your level.'}</p>
                </div>
            );
        }

        return (
            <div className="space-y-6">
                <div className="bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-2xl p-6 border-2 border-indigo-200 dark:border-indigo-700">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">{selectedItem.Name}</h2>
                    <p className="text-indigo-600 dark:text-indigo-400 font-semibold mb-3">{formatAmount(selectedItem.Amount)}</p>
                    <div className="flex flex-wrap gap-2">
                        <span className="px-3 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-full text-xs font-medium">{selectedItem.BankName}</span>
                        {selectedItem.Modeofpay && (
                            <span className="px-3 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-full text-xs font-medium">{selectedItem.Modeofpay}</span>
                        )}
                        <span className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-xs font-medium">Date: {displayDate(selectedItem.Date)}</span>
                        <StatusBadge status={selectedItem.Approvalstatus} />
                    </div>
                </div>

                {isReturned ? renderResubmitForm() : (
                    <>
                        {renderPendingDetails()}
                        <SimpleCommentInput
                            comment={comment}
                            onCommentChange={(e) => setComment(e.target.value)}
                            label="Notes"
                            placeholder="Enter verification notes…"
                            required
                            rows={3}
                            showCharCount
                        />
                        {renderActions()}
                    </>
                )}
            </div>
        );
    };

    return (
        <div className="space-y-6">
            <InboxHeader
                title={`${InboxTitle || 'Bank Withdrawal Verification'} (${queue.length})`}
                subtitle={ModuleDisplayName}
                itemCount={queue.length}
                onBackClick={handleBackToInbox}
                HeaderIcon={HandCoins}
                badgeText="Bank Withdrawal Verification"
                badgeCount={queue.length}
                searchConfig={{
                    enabled: true,
                    placeholder: 'Search by name, bank, mode, no, amount…',
                    value: searchQuery,
                    onChange: (e) => setSearchQuery(e.target.value),
                }}
                enableViewToggle
            />

            <InboxSplitLayout
                isLeftPanelCollapsed={isLeftPanelCollapsed}
                onLeftPanelCollapseToggle={setIsLeftPanelCollapsed}
                isLeftPanelHovered={isLeftPanelHovered}
                onLeftPanelHoverChange={setIsLeftPanelHovered}
                left={{
                    items: filteredQueue,
                    selectedItem,
                    onItemSelect: setSelectedItem,
                    renderItem: renderItemCard,
                    renderListItem,
                    renderCollapsedItem,
                    loading: queueLoading,
                    error: queueError,
                    onRefresh: handleRefresh,
                    config: {
                        title: 'Pending',
                        icon: Clock,
                        emptyMessage: 'No bank withdrawals pending verification!',
                        itemKey: 'Id',
                        enableCollapse: true,
                        enableRefresh: true,
                        enableHover: true,
                        maxHeight: '100%',
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                    },
                    renderPopupContent: () => renderDetailContent(),
                    popupConfig: {
                        title: 'Bank Withdrawal Verification',
                        icon: HandCoins,
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                        maxWidth: 'max-w-[80vw]',
                    },
                }}
                right={{
                    selectedItem,
                    loading: false,
                    renderContent: renderDetailContent,
                    config: {
                        title: 'Withdrawal Details',
                        icon: HandCoins,
                        selectedTitle: 'Bank Withdrawal Verification',
                        emptyTitle: 'No Withdrawal Selected',
                        emptyMessage: 'Select a withdrawal from the list to view details and take action.',
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                        maxHeight: 'calc(100vh-200px)',
                        sticky: true,
                        stickyTop: '1.5rem',
                    },
                }}
            />
        </div>
    );
};

export default VerifyBankWithdrawal;
